/**
 * Three small helpers used by the Client Dossier page:
 *   • AddContractButton — modal form to create a new contract for a client.
 *     Auto-builds the payment schedule via the existing backend engine.
 *   • ContractNotes      — append-only timeline of notes on a single contract.
 *   • CommentsBox        — append-only general comments on the Overview tab
 *     (or any tab — `tab` prop lets us slot comments anywhere).
 */
import React, { useState } from 'react';
import api from '@/lib/api';
import { Plus, MessageSquare, X } from 'lucide-react';

const STRUCTURES = [
  { value: 'monthly_retainer',       label: 'Monthly retainer (advance)' },
  { value: 'quarterly_retainer',     label: 'Quarterly PO-based' },
  { value: 'pdc',                    label: 'Advance + PDCs' },
  { value: 'advance_full',           label: 'Full upfront' },
  { value: 'milestone_linked',       label: 'Milestone-linked' },
  { value: 'variable_consumption',   label: 'Variable / consumption' },
  { value: 'fixed_plus_variable',    label: 'Fixed + variable (MG floor)' },
];

export function AddContractButton({ clientName, divisionId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    entity: '',
    scope_of_work: '',
    start_date: '',
    end_date: '',
    contract_value: '',
    annual_value: '',
    payment_structure: 'monthly_retainer',
    renewal_status: 'active',
    notes: '',
  });

  const submit = async () => {
    if (!f.entity || !f.start_date || !f.end_date || !f.contract_value) {
      alert('Entity, start date, end date and value are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/contracts', {
        client_name: clientName,
        division_id: divisionId,
        entity: f.entity,
        scope_of_work: f.scope_of_work || '',
        start_date: f.start_date,
        end_date: f.end_date,
        contract_value: Number(f.contract_value),
        annual_value: Number(f.annual_value || f.contract_value),
        payment_structure: f.payment_structure,
        renewal_status: f.renewal_status,
        notes: f.notes || '',
        payment_terms_config: f.payment_structure === 'monthly_retainer'
          ? { retainer_day: 1, arrears: false }
          : f.payment_structure === 'quarterly_retainer'
          ? { quarter_anchor_month: 4, arrears: false }
          : {},
      });
      setOpen(false);
      setF({ entity: '', scope_of_work: '', start_date: '', end_date: '',
             contract_value: '', annual_value: '',
             payment_structure: 'monthly_retainer', renewal_status: 'active',
             notes: '' });
      onCreated?.();
    } catch (e) {
      alert('Could not save contract: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md text-white"
        style={{ backgroundColor: '#2A7B88' }}
        data-testid="add-contract-btn"
      >
        <Plus size={12} /> Add Contract
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-contract-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#1B2A4A]">New contract — {clientName}</h3>
              <button onClick={() => setOpen(false)}><X size={14} /></button>
            </div>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <Input label="Contract title / entity*" value={f.entity} onChange={(v) => setF({ ...f, entity: v })} placeholder="e.g. RedBeryl · CC Loyalty · Year 2" testId="contract-entity" />
              <Input label="Scope of work" value={f.scope_of_work} onChange={(v) => setF({ ...f, scope_of_work: v })} placeholder="What we're doing" />
              <Input label="Start date*" type="date" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testId="contract-start" />
              <Input label="End date*" type="date" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testId="contract-end" />
              <div className="grid grid-cols-2 gap-2">
                <Input label="Contract value (₹)*" type="number" value={f.contract_value} onChange={(v) => setF({ ...f, contract_value: v, annual_value: f.annual_value || v })} placeholder="1200000" testId="contract-value" />
                <Input label="Annual value (₹)" type="number" value={f.annual_value} onChange={(v) => setF({ ...f, annual_value: v })} placeholder="auto = contract value" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#6B7280]">Payment structure</label>
                <select value={f.payment_structure} onChange={(e) => setF({ ...f, payment_structure: e.target.value })}
                  className="w-full px-2 py-1.5 border border-[#E5E1DC] rounded text-sm" data-testid="contract-structure">
                  {STRUCTURES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <Input label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} placeholder="Anything important" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E1DC]">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-[#6B7280]">Cancel</button>
              <button onClick={submit} disabled={saving}
                className="px-3 py-1.5 text-xs text-white rounded-md disabled:opacity-50"
                style={{ backgroundColor: '#2D8B5E' }}
                data-testid="submit-contract-btn">
                {saving ? 'Saving…' : 'Create contract'}
              </button>
            </div>
            <p className="text-[10px] text-[#6B7280] pt-1">
              Payment rows will be auto-generated. You can edit them on the Payments tab after creation.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-[#6B7280]">{label}</label>
      <input type={type} value={value || ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-2 py-1.5 border border-[#E5E1DC] rounded text-sm" />
    </div>
  );
}

export function ContractNotes({ contractId, notes = [], onAdded }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      await api.post(`/clients-registry/contracts/${contractId}/notes`, { text: t });
      setText('');
      onAdded?.();
    } catch (e) {
      alert('Could not save note: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const sorted = [...notes].sort((a, b) => (b.logged_at || '').localeCompare(a.logged_at || ''));
  return (
    <div className="border-t border-[#E5E1DC] mt-2 pt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-[11px] text-[#2A7B88] font-medium flex items-center gap-1"
        data-testid={`contract-notes-toggle-${contractId}`}
      >
        <MessageSquare size={11} /> Notes {notes.length > 0 && `(${notes.length})`}
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`contract-notes-body-${contractId}`}>
          {sorted.map((n) => (
            <div key={n.id} className="text-[11px] bg-[#F7F5F2] rounded p-2">
              <p className="text-[#1B2A4A]">{n.text}</p>
              <p className="text-[9px] text-[#6B7280] mt-0.5">
                {n.logged_by} · {new Date(n.logged_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          ))}
          {sorted.length === 0 && <p className="text-[10px] text-[#9CA3AF] italic">No notes yet.</p>}
          <div className="flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Add a note…"
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="flex-1 px-2 py-1.5 border border-[#E5E1DC] rounded text-xs"
              data-testid={`contract-note-input-${contractId}`} />
            <button onClick={submit} disabled={saving || !text.trim()}
              className="px-3 py-1.5 text-xs text-white rounded disabled:opacity-40"
              style={{ backgroundColor: '#2A7B88' }}
              data-testid={`contract-note-submit-${contractId}`}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function CommentsBox({ clientId, comments = [], tab = 'overview', onAdded }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      await api.post(`/clients-registry/${clientId}/comments`, { text: t, tab });
      setText('');
      onAdded?.();
    } catch (e) {
      alert('Could not save: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  const filtered = comments.filter((c) => (c.tab || 'overview') === tab);
  const sorted = [...filtered].sort((a, b) => (b.logged_at || '').localeCompare(a.logged_at || ''));
  return (
    <div className="bg-white rounded-xl p-4 border" style={{ borderColor: '#E5E1DC' }} data-testid={`comments-box-${tab}`}>
      <p className="text-sm font-semibold text-[#1B2A4A] mb-2 flex items-center gap-2">
        <MessageSquare size={14} /> Comments {sorted.length > 0 && <span className="text-[10px] font-normal text-[#6B7280]">({sorted.length})</span>}
      </p>
      <div className="space-y-2 mb-3">
        {sorted.map((c) => (
          <div key={c.id} className="text-xs bg-[#F7F5F2] rounded p-2">
            <p className="text-[#1B2A4A]">{c.text}</p>
            <p className="text-[9px] text-[#6B7280] mt-1">
              {c.logged_by} · {new Date(c.logged_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        ))}
        {sorted.length === 0 && <p className="text-[11px] text-[#9CA3AF] italic">No comments yet.</p>}
      </div>
      <div className="flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment… (append-only, never overwritten)"
          rows={2}
          className="flex-1 px-2 py-1.5 border border-[#E5E1DC] rounded text-xs"
          data-testid={`comment-input-${tab}`} />
        <button onClick={submit} disabled={saving || !text.trim()}
          className="px-3 py-1.5 text-xs text-white rounded self-end disabled:opacity-40"
          style={{ backgroundColor: '#2A7B88' }}
          data-testid={`comment-submit-${tab}`}>Post</button>
      </div>
    </div>
  );
}
